import { angulosDaFoto, PONTOS_DE_REFERENCIA, pontosNecessarios } from '@equiview/domain';
import { markersV1, traitsV1 } from '@equiview/protocol';
import { useMemo, useState, type MouseEvent } from 'react';
import { useParams } from 'react-router';
import { useUrlDaMidia } from '../components/FotoDaMidia.js';
import { SemCadastro } from '../components/SemCadastro.js';
import { Aguardar, Botao, BotaoLink, CabecalhoPagina, classeTd, classeTh, EstadoVazio, Painel, Seletor, Tabela } from '../components/ui/index.js';
import { formatarValor } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, MedicaoPorFoto, Medida, Midia, PontosMarcados } from '../dados/fonte.js';

const ANGULOS = traitsV1.filter((t) => t.kind === 'angulo').sort((a, b) => a.sortOrder - b.sortOrder);
const ORDEM = pontosNecessarios(ANGULOS, markersV1.map((m) => m.code));
const ROTULO: Record<string, string> = Object.fromEntries([
  ...markersV1.map((m) => [m.code, m.label]),
  ...PONTOS_DE_REFERENCIA.map((p) => [p.codigo, p.rotulo]),
]);

/**
 * Fotogrametria (Fase 1): marcar os pontos na foto de perfil e ler os 11
 * angulos. Feito no escritorio, com calma e zoom — e comparado depois com o
 * goniometro do campo, no relatorio de repetibilidade.
 */
export function Fotogrametria() {
  const { id = '', midiaId = '' } = useParams();
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(
    async (f) => {
      const [animal, midia] = await Promise.all([f.animal(id), f.midia(midiaId)]);
      if (!animal || !midia || !f.cadastro) return null;
      const [medicao, medidas] = await Promise.all([f.cadastro.medicaoPorFoto(midia.assessment_id), f.medidas([midia.assessment_id])]);
      return { animal, midia, medicao, medidas };
    },
    [id, midiaId],
  );

  if (!fonte.cadastro || !equipe) return <SemCadastro demo={fonte.ficticia} />;

  return (
    <Aguardar consulta={consulta}>
      {(d) =>
        !d || d.midia.kind !== 'foto' ? (
          <EstadoVazio titulo="Foto não encontrada" acao={<BotaoLink to={`/animais/${id}`}>Voltar à ficha</BotaoLink>} />
        ) : (
          <Marcacao key={d.midia.id} {...d} />
        )
      }
    </Aguardar>
  );
}

function Marcacao({ animal, midia, medicao, medidas }: { animal: Animal; midia: Midia; medicao: MedicaoPorFoto; medidas: Medida[] }) {
  const cadastro = useFonte().cadastro!;
  const url = useUrlDaMidia(midia);
  // Pontos de outra foto (remarcacao numa foto nova) nao servem para esta.
  const [pontos, setPontos] = useState<PontosMarcados>(medicao.midiaId === midia.id ? medicao.pontos : {});
  const [ativo, setAtivo] = useState<string>(() => ORDEM.find((c) => !pontos[c]) ?? ORDEM[0]!);
  const [zoom, setZoom] = useState<'1' | '2' | '3'>('1');
  const [proporcao, setProporcao] = useState(1);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<string | null>(null);

  const angulos = useMemo(() => angulosDaFoto(pontos, ANGULOS, proporcao), [pontos, proporcao]);
  const campo = (code: string) => medidas.find((m) => m.trait_code === code && m.side === 'NA')?.value_num ?? null;
  const marcados = ORDEM.filter((c) => pontos[c]).length;

  const marcar = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const ponto = { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
    const novos = { ...pontos, [ativo]: ponto };
    setPontos(novos);
    setMensagem(null);
    // Segue para o proximo ponto que falta, na ordem.
    const i = ORDEM.indexOf(ativo);
    const proximo = [...ORDEM.slice(i + 1), ...ORDEM.slice(0, i)].find((c) => !novos[c]);
    if (proximo) setAtivo(proximo);
  };

  const salvar = async () => {
    setSalvando(true);
    setMensagem(null);
    try {
      await cadastro.salvarMedicaoPorFoto(midia.assessment_id, midia.id, pontos, angulos);
      setMensagem('Ângulos salvos. Eles sobem na próxima sincronização.');
    } catch (e) {
      setMensagem(e instanceof Error ? e.message : String(e));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[
          { rotulo: 'Plantel', para: '/animais' },
          { rotulo: animal.name, para: `/animais/${animal.id}` },
          { rotulo: 'Ângulos na foto' },
        ]}
        titulo="Ângulos na foto de perfil"
        meta={
          <span>
            Marque cada ponto clicando na foto. {marcados}/{ORDEM.length} marcados
            {medicao.autor ? ` · última medição por ${medicao.autor}` : ''}.
          </span>
        }
        acoes={
          <Botao variante="principal" carregando={salvando} disabled={marcados === 0} onClick={() => void salvar()}>
            Salvar ângulos
          </Botao>
        }
      />
      {mensagem && <p className="border-l-2 border-destaque px-3 py-2 text-sm">{mensagem}</p>}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Painel
          titulo={`Ponto da vez: ${ativo} — ${ROTULO[ativo] ?? ''}`}
          corpo="p-0"
          acoes={
            <Seletor
              rotulo="Zoom"
              valor={zoom}
              onMudar={setZoom}
              opcoes={(['1', '2', '3'] as const).map((z) => ({ valor: z, conteudo: <span className="px-1 py-1">{z}×</span> }))}
            />
          }
        >
          {!url ? (
            <p className="p-4 text-sm text-apagado">
              O arquivo da foto não está neste aparelho nem acessível agora. Conecte-se à internet para abrir a foto.
            </p>
          ) : (
            <div className="max-h-[75vh] overflow-auto bg-abismo">
              <div
                role="application"
                aria-label="Foto para marcar os pontos"
                className="relative cursor-crosshair"
                style={{ width: `${Number(zoom) * 100}%` }}
                onClick={marcar}
              >
                <img
                  src={url}
                  alt="Perfil esquerdo"
                  draggable={false}
                  className="block w-full select-none"
                  onLoad={(e) => setProporcao(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || 1)}
                />
                {Object.entries(pontos).map(([codigo, p]) => (
                  <span
                    key={codigo}
                    className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                  >
                    <span className={`block size-3 rounded-full border-2 border-white ${codigo === ativo ? 'bg-latao' : 'bg-destaque'}`} />
                    <span className="absolute top-3 left-2 bg-abismo/80 px-1 font-mono text-[10px] text-white">{codigo}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Painel>

        <div className="space-y-6">
          <Painel titulo="Pontos" corpo="p-0">
            <ul className="max-h-80 divide-y divide-borda overflow-y-auto text-sm">
              {ORDEM.map((codigo) => (
                <li key={codigo}>
                  <button
                    type="button"
                    onClick={() => setAtivo(codigo)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left ${codigo === ativo ? 'bg-destaque-fundo' : ''}`}
                  >
                    <span>
                      <span className="font-mono font-semibold">{codigo}</span> <span className="text-apagado">{ROTULO[codigo]}</span>
                    </span>
                    <span className={pontos[codigo] ? 'text-ok' : 'text-apagado'}>{pontos[codigo] ? '✓' : '—'}</span>
                  </button>
                </li>
              ))}
            </ul>
            {pontos[ativo] && (
              <div className="border-t border-borda p-3">
                <Botao
                  tamanho="sm"
                  variante="discreto"
                  onClick={() => {
                    const { [ativo]: _, ...resto } = pontos;
                    setPontos(resto);
                  }}
                >
                  Limpar {ativo}
                </Botao>
              </div>
            )}
          </Painel>

          <Painel titulo="Ângulos" corpo="p-0">
            <Tabela rotulo="Ângulos">
              <thead>
                <tr>
                  <th scope="col" className={classeTh}>Ângulo</th>
                  <th scope="col" className={`${classeTh} text-right`}>Foto</th>
                  <th scope="col" className={`${classeTh} text-right`}>Campo</th>
                </tr>
              </thead>
              <tbody>
                {ANGULOS.map((t) => {
                  const f = angulos[t.code];
                  const c = campo(t.code);
                  return (
                    <tr key={t.code}>
                      <td className={classeTd}>{t.label}</td>
                      <td className={`${classeTd} numero text-right`}>{f == null ? '—' : `${formatarValor(f, t)}°`}</td>
                      <td className={`${classeTd} numero text-right text-apagado`}>
                        {c == null ? '—' : `${formatarValor(c, t)}°`}
                        {f != null && c != null && <span className="block text-xs">{f - c > 0 ? '+' : ''}{formatarValor(f - c, t)}°</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
            <p className="border-t border-borda px-3 py-2 text-xs text-apagado">
              As inclinações usam a linha do solo marcada (SOLO_1 e SOLO_2); sem ela, a horizontal da foto.
            </p>
          </Painel>
        </div>
      </div>
    </div>
  );
}
